"use client";

import { Button } from "@/components/atoms/buttons";
import { toast } from "@/components/atoms/toast";
import { Graphics } from "@/components/graphics";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { sdk } from "@/lib/medusa/client";
import { Loader2 } from "lucide-react";
import { useState } from "react";

type GoogleSignInButtonProps = {
  locale: Locales;
};

const GoogleSignInButton = ({ locale }: GoogleSignInButtonProps) => {
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const showGoogleLoginError = () => {
    const message = __("Google login failed", locale);

    setErrorMessage(message);
    toast.error({ title: message });
  };

  async function loginWithGoogle() {
    setIsPending(true);
    setErrorMessage(null);

    try {
      try {
        await sdk.auth.logout();
      } catch (error) {
        console.error("Google logout before login failed:", error);
      }

      const callbackUrl =
        process.env.NEXT_PUBLIC_GOOGLE_CALLBACK_URL || `${window.location.origin}/auth/google-redirect`;
      const result = await sdk.auth.login("customer", "google", {
        callback_url: callbackUrl,
      });

      if (typeof result === "object" && result.location) {
        window.location.assign(result.location);
        return;
      }

      showGoogleLoginError();
    } catch (error) {
      console.error("Google login failed:", error);
      showGoogleLoginError();
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="space-y-2">
      <Button
        onClick={loginWithGoogle}
        disabled={isPending}
        className="max-h-12 w-full text-base font-semibold"
        type="button"
        variant="light"
      >
        {isPending ? <Loader2 className="animate-spin" /> : <Graphics.google />}
        {__("Sign in with Google", locale)}
      </Button>
      {errorMessage && (
        <p className="text-sm font-medium text-red-600" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default GoogleSignInButton;
